// Qué hace el login con una EXCEPCIÓN de sus server actions (no con un resultado
// `{ ok: false }`, que es una respuesta normal: credenciales, código vencido…).
//
// S5-4 (P1-17 · Sentry REDY-X, 2.060 eventos «no manejado»): cuando el POST de
// una action no vuelve con flight RSC, `server-action-reducer.js` de Next 16
// lanza `Error("An unexpected response was received from the server.")` (código
// E394). Medido el 2026-10-10: los 34 eventos retenidos de /login eran un bot
// (UA de AOL 9 sobre Windows XP/Vista) que pulsaba «Enviar código» y recibía un
// 403 de la plataforma de Vercel — no de Next, que responde sus 403 CON flight.
// Los handlers no tenían `catch`, así que el rechazo salía sin manejar y, en
// verificar código y contraseña, el botón se quedaba girando para siempre.
//
// Este módulo solo decide QUÉ pasó; `app/(auth)/login/loginHandlers.ts` decide
// qué hacer. Puro a propósito: se prueba sin DOM.
import { isVersionSkewError } from "@/lib/deploy/versionSkew"

export type LoginFailureKind = "network" | "version_skew" | "server" | "unknown"

// Las frases con que cada navegador rechaza un fetch que no llegó (Chrome,
// Safari, Firefox, WebView). Mismo corpus que `ignoreErrors` de
// instrumentation-client.ts.
const NETWORK_MESSAGE =
  /failed to fetch|load failed|networkerror|network error|network request failed|network connection was lost|appears to be offline/i
const UNEXPECTED_RESPONSE = /an unexpected response was received from the server/i

/**
 * @param online `navigator.onLine` en el momento del fallo. `false` es fiable
 *   (no hay red); `true` no dice nada (puede haber Wi-Fi sin salida).
 */
export function classifyLoginFailure(err: unknown, online?: boolean): LoginFailureKind {
  // Primero el desfase: recargar lo arregla aunque además se haya caído la red.
  if (isVersionSkewError(err)) return "version_skew"
  if (online === false) return "network"
  const message = err instanceof Error ? err.message : typeof err === "string" ? err : ""
  if (err instanceof TypeError && NETWORK_MESSAGE.test(message)) return "network"
  const code =
    typeof err === "object" && err !== null
      ? (err as { __NEXT_ERROR_CODE?: unknown }).__NEXT_ERROR_CODE
      : undefined
  if (code === "E394" || UNEXPECTED_RESPONSE.test(message)) return "server"
  return "unknown"
}

/**
 * Solo lo inesperado va a Sentry. Red caída es ruido irreducible (ya está en
 * `ignoreErrors`) y el desfase lo descarta `beforeSend`; reportarlos sería
 * pagar cuota por eventos que nadie puede arreglar.
 */
export function shouldReportLoginFailure(kind: LoginFailureKind): boolean {
  return kind === "server" || kind === "unknown"
}

/** Clave de `auth.login.*` con el mensaje que ve la persona. */
export const LOGIN_FAILURE_MESSAGE_KEY = {
  network: "failureNetwork",
  version_skew: "failureVersion",
  server: "failureServer",
  unknown: "failureUnknown",
} as const satisfies Record<LoginFailureKind, string>
